import SEO from "@/components/SEO";
import { Layers, Sparkles, ShieldCheck, Compass, ScrollText } from "lucide-react";

const CRITERIA = [
  {
    k: "Inventory",
    weight: 20,
    icon: Layers,
    desc: "Breadth of US properties — chain flagships, indie boutiques, every star level. We score how often the operator is the only one offering a given hotel, and how often it's the cheapest of the operators that do offer it.",
  },
  {
    k: "Loyalty value",
    weight: 25,
    icon: Sparkles,
    desc: "We compute the implied cents-per-point earn rate, the average redemption value, breakage risk, and the real-world value of mid-tier elite perks (free breakfast, late checkout, suite upgrades). Programs with published award charts and no devaluations in the last 24 months earn extra credit.",
  },
  {
    k: "Cancellation",
    weight: 20,
    icon: ShieldCheck,
    desc: "How forgiving the operator's standard policy is, the proportion of inventory available with free cancellation, and how cleanly refunds process when things go wrong.",
  },
  {
    k: "Customer support",
    weight: 15,
    icon: Compass,
    desc: "Response time, escalation paths, weekend coverage, and a real test: we mystery-shop one disrupted booking per operator per year.",
  },
  {
    k: "Editorial trust",
    weight: 20,
    icon: ScrollText,
    desc: "Transparent total pricing (taxes + resort fees), honest listings, no shady up-sells or fake-urgency banners, and clear ownership disclosure.",
  },
];

export default function MethodologyPage() {
  return (
    <div>
      <SEO title="Methodology" description="How Choose My Hotel scores US hotel booking sources — five criteria, public weights, refreshed every twelve months." />

      <section className="bg-charcoal text-sand relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay opacity-30" />
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full blur-3xl bg-terracotta/30 pointer-events-none" />
        <div className="absolute -bottom-40 -left-20 w-96 h-96 rounded-full blur-3xl bg-forest/40 pointer-events-none" />
        <div className="relative container-x py-20 md:py-28 max-w-4xl">
          <div className="overline text-terracotta-light">Methodology</div>
          <h1 className="mt-4 font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight text-balance">
            Five criteria, public weights, no sponsor influence.
          </h1>
          <p className="mt-6 text-sand/80 text-lg leading-relaxed max-w-2xl">
            Every operator we cover is scored against the same five-factor framework. Scores are reviewed every twelve months and the previous edition stays available so changes are visible.
          </p>
        </div>
      </section>

      <section className="container-x py-16 md:py-20 max-w-5xl">
        <div className="space-y-6 md:space-y-8">
          {CRITERIA.map((c, i) => {
            const Ico = c.icon;
            return (
              <div key={c.k} className="grid md:grid-cols-12 gap-6 md:gap-10 bg-white border border-charcoal/10 p-7 md:p-9">
                <div className="md:col-span-3 flex md:flex-col items-start gap-4">
                  <div className="font-mono text-sm text-terracotta">0{i + 1}</div>
                  <Ico size={22} className="text-terracotta" />
                  <div className="md:mt-auto">
                    <div className="overline">Weight</div>
                    <div className="font-serif text-4xl mt-1">{c.weight}<span className="text-base text-charcoal/50">%</span></div>
                  </div>
                </div>
                <div className="md:col-span-9">
                  <h2 className="font-serif text-3xl md:text-4xl leading-tight">{c.k}</h2>
                  <p className="mt-4 text-charcoal/80 leading-relaxed">{c.desc}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-14 bg-cream border-l-4 border-terracotta p-7 md:p-9">
          <div className="overline mb-2">Disclosure</div>
          <p className="text-charcoal/80 leading-relaxed">
            Choose My Hotel is funded by editorial affiliate partnerships. Some outbound links may earn us a commission when you book — at no cost to you, and never affecting our scores. A property's rank, an operator's score, and the placement of any review have never been for sale and never will be.
          </p>
        </div>
      </section>
    </div>
  );
}
